import { generateBoutiqueLogoHTML } from "../components/boutiques/BoutiqueLogo";

const TicketPrintService = {
  
  /**
   * Impression intelligente selon la plateforme
   */
 async printTicket(saleData, boutiqueInfo = {}, createdBy = null) {
  try {
    
    
    const support = this.checkPrintSupport();

    if (support.isAndroid) {
      return await this.printTicketAndroid(saleData, boutiqueInfo, createdBy);
    } else if (support.isMobile) {
      return await this.printTicketIOS(saleData, boutiqueInfo, createdBy);
    } else {
      return await this.printTicketDesktop(saleData, boutiqueInfo, createdBy);
    }
    
  } catch (error) {
    console.error('❌ Erreur impression générale:', error);
    return { 
      success: false, 
      message: 'Erreur lors de l\'impression: ' + error.message 
    };
  }
},

  /**
   * Impression optimisée pour Android
   */
  async printTicketAndroid(saleData, boutiqueInfo, createdBy) {
    try {
      
      const printWindow = window.open('', '_blank', 'width=302,height=600');
      
      if (!printWindow) {
        throw new Error('Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez que les popups ne sont pas bloqués.');
      }

      // HTML optimisé pour Android
      const androidOptimizedHtml = `
        <!DOCTYPE html>
        <html lang="fr">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
          <title>Ticket de Caisse</title>
          <style>
            ${this.getAndroidPrintStyles()}
          </style>
        </head>
        <body>
          ${this.getCloseButtonHTML()}
          ${this.getTicketBody(saleData, boutiqueInfo, createdBy)}
          
          <script>
            // Auto-impression avec délai pour Android
            setTimeout(function() {
              window.print();
            }, 1000);
            
            // Fermeture automatique après impression
            window.addEventListener('afterprint', function() {
              setTimeout(function() {
                window.close();
              }, 500);
            });
            
            // ℹ️ Fermeture manuelle disponible via le bouton ✕ en haut à droite
            // (plus de fermeture forcée après 10s qui bloquait l'utilisateur)
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(androidOptimizedHtml);
      printWindow.document.close();

      return { success: true };
      
    } catch (error) {
      console.error('❌ Erreur impression Android:', error);
      return { 
        success: false, 
        message: 'Erreur lors de l\'impression sur tablette: ' + error.message 
      };
    }
  },

  /**
   * Impression pour iOS avec fallback
   */
  async printTicketIOS(saleData, boutiqueInfo) {
    try {
      const ticketText = this.generatePlainTextTicket(saleData, boutiqueInfo);
      
      // URL scheme pour ouvrir directement TM Print Assistant
      const tmPrintUrl = `tmprint://print?text=${encodeURIComponent(ticketText)}`;
      
      window.location.href = tmPrintUrl;
      
      return { success: true };
    } catch (error) {
      // Fallback vers méthode classique
      return await this.printTicketDesktop(saleData, boutiqueInfo);
    }
  },

  /**
   * Impression classique pour desktop
   */
  async printTicketDesktop(saleData, boutiqueInfo, createdBy) {
    try {
      const ticketHtml = this.generateTicketHTML(saleData, boutiqueInfo, createdBy);
      
      const printWindow = window.open('', '_blank', 'width=302,height=600');
      
      if (!printWindow) {
        throw new Error('Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez que les popups ne sont pas bloqués.');
      }

      printWindow.document.write(ticketHtml);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          
          printWindow.onafterprint = () => {
            printWindow.close();
          };
          
          setTimeout(() => {
            if (!printWindow.closed) {
              printWindow.close();
            }
          }, 1000);
        }, 500);
      };

      return { success: true };
      
    } catch (error) {
      console.error('❌ Erreur impression desktop:', error);
      return { 
        success: false, 
        message: 'Erreur lors de l\'impression: ' + error.message 
      };
    }
  },

  /**
   * ✅ NOUVEAU : Bouton de fermeture flottant
   * - Visible à l'écran (mobile, tablette, desktop)
   * - Masqué automatiquement à l'impression via @media print
   * - Taille 44x44px (recommandation tactile Apple/Google)
   */
  getCloseButtonHTML() {
    return `
      <button 
        id="ticket-close-btn"
        onclick="window.close()" 
        aria-label="Fermer"
        title="Fermer"
      >
        ✕
      </button>
      <style>
        #ticket-close-btn {
          position: fixed;
          top: 12px;
          right: 12px;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: none;
          color: black;
          font-size: 20px;
          font-weight: bold;
          cursor: pointer;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.15s ease, background 0.15s ease;
          -webkit-tap-highlight-color: transparent;
          font-family: Arial, sans-serif;
          line-height: 1;
        }
        #ticket-close-btn:hover,
        #ticket-close-btn:active {
          transform: scale(1.08);
        }
        /* ✅ CRITIQUE : Masquer le bouton à l'impression pour ne pas l'imprimer sur le ticket */
        @media print {
          #ticket-close-btn {
            display: none !important;
          }
        }
      </style>
    `;
  },

  /**
   * Styles CSS optimisés pour l'impression Android
   */
  getAndroidPrintStyles() {
    return `
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      @page {
        size: 80mm auto;
        margin: 0;
      }

      body {
        font-family: 'Arial', 'Helvetica', sans-serif;
        font-size: 12px;
        line-height: 1.2;
        background: white;
        color: black;
        width: 80mm;
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .ticket {
        width: 100%;
        padding: 4px;
        background: white;
      }

      .header {
        text-align: center;
        margin-bottom: 6px;
      }

      .logo {
        font-size: 24px;
        margin-bottom: 2px;
      }

      .shop-name {
        font-size: 14px;
        font-weight: bold;
        margin-bottom: 2px;
      }

      .address, .phone, .email {
        font-size: 10px;
        margin-bottom: 1px;
      }

      .separator {
        text-align: center;
        margin: 4px 0;
        font-size: 10px;
      }

      .sale-info {
        margin-bottom: 6px;
      }

      .sale-info .row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1px;
        font-size: 10px;
      }

      .items-header {
        font-weight: bold;
        margin-bottom: 3px;
        font-size: 11px;
        text-align: center;
      }

      .item {
        margin-bottom: 2px;
        font-size: 10px;
      }

      .item-name {
        font-weight: bold;
        margin-bottom: 1px;
      }

      .item-details {
        display: flex;
        justify-content: space-between;
        font-size: 9px;
      }

      .totals {
        margin-top: 6px;
        border-top: 1px solid #000;
        padding-top: 3px;
      }

      .total-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1px;
        font-size: 11px;
      }

      .total-row.final {
        font-weight: bold;
        font-size: 13px;
        border-top: 1px solid #000;
        padding-top: 2px;
        margin-top: 2px;
      }

      .payment-info {
        margin-top: 4px;
        text-align: center;
      }

      .payment-method {
        font-weight: bold;
        font-size: 11px;
        margin-bottom: 2px;
      }

      .credit {
        background: #000;
        color: #fff;
        padding: 2px;
        text-align: center;
        font-weight: bold;
        font-size: 10px;
        margin: 4px 0;
      }

      .notes {
        margin: 4px 0;
        padding: 3px;
        border: 1px solid #ccc;
        font-size: 9px;
      }

      .footer {
        text-align: center;
        margin-top: 6px;
      }

      .thank-you {
        font-weight: bold;
        font-size: 11px;
        margin-bottom: 2px;
      }

      .return-policy {
        font-size: 8px;
        margin-bottom: 1px;
      }

      .qr-placeholder {
        margin-top: 6px;
        padding: 6px;
        border: 1px dashed #ccc;
        text-align: center;
      }

      .qr-code {
        font-size: 18px;
        margin-bottom: 3px;
      }

      .qr-placeholder p {
        font-size: 7px;
      }

      .cut-line {
        text-align: center;
        margin-top: 12px;
        font-size: 9px;
        color: #999;
      }
    `;
  },

  /**
   * Styles CSS améliorés pour le ticket (VERSION AMÉLIORÉE)
   */
  getTicketStyles() {
    return `
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        /* ✅ POLICE AMÉLIORÉE : Plus lisible que Courier New */
        font-family: 'Arial', 'Helvetica', 'Liberation Sans', system-ui, sans-serif;
        /* ✅ TAILLE LÉGÈREMENT AUGMENTÉE */
        font-size: 15px;
        line-height: 1.4;
        background: white;
        color: black;
        width: 80mm;
        margin: 0;
        padding: 0;
        /* ✅ AMÉLIORATION DE LA NETTETÉ */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      .ticket {
        width: 100%;
        padding: 8px;
        background: white;
      }

      .header {
        text-align: center;
        margin-bottom: 8px;
      }

      .logo {
        font-size: 30px;
        margin-bottom: 4px;
      }

      .shop-name {
        /* ✅ TAILLE AUGMENTÉE POUR MEILLEURE LISIBILITÉ */
        font-size: 17px;
        font-weight: bold;
        margin-bottom: 4px;
        /* ✅ ESPACEMENT DES LETTRES POUR PLUS DE CLARTÉ */
        letter-spacing: 0.3px;
      }

      .address, .phone, .email {
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 12px;
        margin-bottom: 2px;
        /* ✅ POIDS DE POLICE LÉGÈREMENT RENFORCÉ */
        font-weight: 500;
      }

      .separator {
        text-align: center;
        margin: 6px 0;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 13px;
      }

      .sale-info {
        margin-bottom: 8px;
      }

      .sale-info .row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 2px;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 12px;
        /* ✅ POIDS RENFORCÉ POUR PLUS DE CLARTÉ */
        font-weight: 600;
      }

      .items-header {
        font-weight: bold;
        margin-bottom: 4px;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 14px;
        text-align: center;
      }

      .item {
        margin-bottom: 3px;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 12px;
      }

      .item-name {
        font-weight: bold;
        margin-bottom: 1px;
        /* ✅ COULEUR PLUS CONTRASTÉE */
        color: #000;
      }

      .item-details {
        display: flex;
        justify-content: space-between;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 11px;
        /* ✅ POIDS RENFORCÉ */
        font-weight: 500;
      }

      .totals {
        margin-top: 8px;
        border-top: 1px solid #000;
        padding-top: 4px;
      }

      .total-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 2px;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 13px;
        /* ✅ POIDS RENFORCÉ */
        font-weight: 600;
      }

      .total-row.final {
        font-weight: bold;
        /* ✅ TAILLE AUGMENTÉE POUR LE TOTAL FINAL */
        font-size: 16px;
        border-top: 1px solid #000;
        padding-top: 3px;
        margin-top: 3px;
      }

      .payment-info {
        margin-top: 6px;
        text-align: center;
      }

      .payment-method {
        font-weight: bold;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 13px;
        margin-bottom: 3px;
      }

      .credit {
        background: #000;
        color: #fff;
        padding: 3px;
        text-align: center;
        font-weight: bold;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 12px;
        margin: 6px 0;
      }

      .notes {
        margin: 6px 0;
        padding: 4px;
        border: 1px solid #ccc;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 11px;
      }

      .footer {
        text-align: center;
        margin-top: 8px;
      }

      .thank-you {
        font-weight: bold;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 14px;
        margin-bottom: 3px;
      }

      .return-policy {
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 10px;
        margin-bottom: 2px;
        /* ✅ POIDS RENFORCÉ */
        font-weight: 500;
      }

      .qr-placeholder {
        margin-top: 8px;
        padding: 8px;
        border: 1px dashed #ccc;
        text-align: center;
      }

      .qr-code {
        font-size: 20px;
        margin-bottom: 4px;
      }

      .qr-placeholder p {
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 9px;
        /* ✅ POIDS RENFORCÉ */
        font-weight: 500;
      }

      .cut-line {
        text-align: center;
        margin-top: 16px;
        /* ✅ TAILLE AUGMENTÉE */
        font-size: 11px;
        color: #999;
        /* ✅ ESPACEMENT DES CARACTÈRES */
        letter-spacing: 1px;
      }

      /* ✅ AMÉLIORATION SPÉCIFIQUE POUR L'IMPRESSION */
      @media print {
        body {
          /* ✅ FORCE UNE POLICE CLAIRE POUR L'IMPRESSION */
          font-family: Arial, Helvetica, sans-serif !important;
          margin: 0;
          padding: 0;
        }
        
        .ticket {
          margin: 0;
          padding: 4px;
        }
        
        .cut-line {
          page-break-after: always;
        }
        
        /* ✅ OPTIMISATION DES CONTRASTES POUR L'IMPRESSION */
        .shop-name,
        .item-name,
        .total-row.final {
          color: #000 !important;
        }
      }

      /* ✅ AMÉLIORATION POUR MOBILE */
      @media screen and (max-width: 480px) {
        body {
          font-size: 16px;
        }
        
        .shop-name {
          font-size: 18px;
        }
        
        .total-row.final {
          font-size: 17px;
        }
      }
    `;
  },

  /**
   * Génère le corps du ticket HTML
   */
  getTicketBody(saleData, boutiqueInfo, createdBy) {
  const {
    customer,
    items = [], // ✅ CORRECTION: Valeur par défaut array vide
    paymentMethod,
    isCredit,
    notes,
    saleNumber = this.generateSaleNumber(),
    date = new Date(),
    totals // ✅ AJOUT: Support du format avec totals
  } = saleData;

  // ✅ CORRECTION: Récupérer discount depuis plusieurs sources possibles
  const discount = parseFloat(saleData.discount) || parseFloat(totals?.discount) || 0;
  
  

  // ✅ CORRECTION: S'assurer que items est un array
  const safeItems = Array.isArray(items) ? items : [];

  const subtotal = this.calculateSubtotal(safeItems);
  const total = subtotal - discount;

  return `
<div class="ticket">
  <!-- En-tête -->
  <div class="header">
${generateBoutiqueLogoHTML(boutiqueInfo, { width: 140, height: 80 })}
    <div class="shop-name">${boutiqueInfo.name || 'MA BOUTIQUE'}</div>
    <div class="address">${boutiqueInfo.address || 'Dakar, Sénégal'}</div>
    <div class="phone">${
      boutiqueInfo.phone1 
        ? `${boutiqueInfo.phone || '+221 XX XXX XX XX'} | ${boutiqueInfo.phone1}`
        : (boutiqueInfo.phone || '+221 XX XXX XX XX')
    }</div>
    ${boutiqueInfo.email ? `<div class="email">${boutiqueInfo.email}</div>` : ''}
  </div>

  <div class="separator">================================</div>

  <!-- Informations de vente -->
  <div class="sale-info">
    <div class="row">
      <span>N° Ticket:</span>
      <span>#${saleNumber}</span>
    </div>
    <div class="row">
      <span>Date:</span>
      <span>${this.formatDate(date)}</span>
    </div>
    <div class="row">
      <span>Heure:</span>
      <span>${this.formatTime(date)}</span>
    </div>
    <div class="row">
      <span>Client:</span>
      <span>${customer?.name || 'Client anonyme'}</span>
    </div>
    ${customer?.phone ? `
    <div class="row">
      <span>Tél:</span>
      <span>${customer.phone}</span>
    </div>` : ''}
    ${customer?.adresse ? `
    <div class="row">
      <span>Tél:</span>
      <span>${customer.addresse}</span>
    </div>` : ''}
    ${createdBy ? `
    <div class="row">
      <span>Vendeur:</span>
      <span>${createdBy.name || createdBy.firstName + ' ' + createdBy.lastName || 'Employé'}</span>
    </div>` : ''}
  </div>

  <div class="separator">--------------------------------</div>

  <!-- Articles -->
  <div class="items-header">ARTICLES</div>
  
  ${safeItems.length > 0 ? safeItems.map(item => `
  <div class="item">
    <div class="item-name">${item.name || item.productName || 'Produit'}</div>
    <div class="item-details">
      <span>${item.quantity || 0} × ${this.formatCurrency(item.price || item.unitPrice || 0)}</span>
      <span>${this.formatCurrency((item.quantity || 0) * (item.price || item.unitPrice || 0))}</span>
    </div>
  </div>
  `).join('') : '<div class="item"><div class="item-name">Aucun article</div></div>'}
  

  <!-- Totaux -->
  <div class="totals">
    <div class="total-row">
      <span>Sous-total:</span>
      <span>${this.formatCurrency(subtotal)}</span>
    </div>
    
    ${discount > 0 ? `
    <div class="total-row">
      <span>Remise:</span>
      <span>-${this.formatCurrency(discount)}</span>
    </div>` : ''}
    
    <div class="total-row final">
      <span>TOTAL:</span>
      <span>${this.formatCurrency(total)}</span>
    </div>
  </div>

  <!-- Mode de paiement -->
  <div class="payment-info">
    <div class="payment-method">
      ${this.getPaymentMethodLabel(paymentMethod)}
    </div>
    ${isCredit ? `
    <div class="credit">
      ⚠️ VENTE À CRÉDIT
    </div>
    ` : ''}
  </div>

  ${notes ? `
    <div class="separator">--------------------------------</div>
    <div class="notes">
      <strong>Notes:</strong><br>
      ${notes}
    </div>
    ` : ''}

    <div class="separator">================================</div>


  <!-- Pied de page -->
  <div class="footer">
      <p class="thank-you">Merci de votre visite !</p>
     <p class="return-policy"><strong>${boutiqueInfo.name || 'Notre boutique'}</strong> vous remercie</p>
      <p class="return-policy">pour la confiance renouvelée</p>
      
      <!--div class="qr-placeholder">
        <div class="qr-code">📱</div>
        <p>Scannez pour nous suivre</p>
      </div>
    </div-->

    <div class="cut-line">- - - - - - - - - - - - - - - - - -</div>
  </div>
  `;
},


  /**
   * Génère un ticket en texte brut pour iOS
   */
  generatePlainTextTicket(saleData, boutiqueInfo) {
    const {
      customer,
      items,
      paymentMethod,
      isCredit,
      notes,
      saleNumber = this.generateSaleNumber(),
      date = new Date(),
      totals // ✅ AJOUT: Support du format avec totals
    } = saleData;

    // ✅ CORRECTION: Récupérer discount avec parseFloat
    const discount = parseFloat(saleData.discount) || parseFloat(totals?.discount) || 0;

    const subtotal = this.calculateSubtotal(items);
    const total = subtotal - discount;

    let ticket = '';
    ticket += '================================\n';
    ticket += `${boutiqueInfo.name || 'MA BOUTIQUE'}\n`;
    ticket += `${boutiqueInfo.address || 'Dakar, Sénégal'}\n`;
    if (boutiqueInfo.phone1) {
      // Si phone1 existe, afficher les deux numéros séparés par " | "
      ticket += `${boutiqueInfo.phone || '+221 XX XXX XX XX'} | ${boutiqueInfo.phone1}\n`;
    } else {
      // Si phone1 n'existe pas, centrer le numéro principal
      const phoneText = boutiqueInfo.phone || '+221 XX XXX XX XX';
      const padding = Math.max(0, Math.floor((32 - phoneText.length) / 2));
      ticket += `${' '.repeat(padding)}${phoneText}\n`;
    }
    if (boutiqueInfo.email) ticket += `${boutiqueInfo.email}\n`;
    ticket += '================================\n\n';
    
    ticket += `N° Ticket: #${saleNumber}\n`;
    ticket += `Date: ${this.formatDate(date)}\n`;
    ticket += `Heure: ${this.formatTime(date)}\n`;
    ticket += `Client: ${customer?.name || 'Client anonyme'}\n`;
    if (customer?.phone) ticket += `Tél: ${customer.phone}\n`;
    ticket += '--------------------------------\n';
    
    // Articles
    ticket += 'ARTICLES\n';
    items.forEach(item => {
      const unitPrice = item.unitPrice || item.unit_price || item.price || 0;
      const totalPrice = unitPrice * item.quantity;
      ticket += `${item.name || item.productName || item.product_name || 'Article'}\n`;
      ticket += `${item.quantity} × ${this.formatPrice(unitPrice)} = ${this.formatPrice(totalPrice)}\n`;
    });
    
    ticket += '--------------------------------\n';
    ticket += `Sous-total: ${this.formatPrice(subtotal)}\n`;
    if (discount > 0) {
      ticket += `Remise: -${this.formatPrice(discount)}\n`;
    }
    ticket += `TOTAL: ${this.formatPrice(total)}\n`;
    ticket += '================================\n';
    
    ticket += `Paiement: ${this.getPaymentMethodLabel(paymentMethod)}\n`;
    if (isCredit) {
      ticket += '⚠️ VENTE À CRÉDIT\n';
    }
    
    if (notes) {
      ticket += '--------------------------------\n';
      ticket += `Notes: ${notes}\n`;
    }
    
    ticket += '================================\n';
    ticket += 'Merci de votre visite !\n';
    ticket += 'Échange possible sous 7 jours\n';
    ticket += 'avec ce ticket\n';
    ticket += '================================\n';
    
    return ticket;
  },

  /**
   * Détection de l'environnement mobile
   */
  isMobileDevice() {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },

  /**
   * Détection spécifique Android
   */
  isAndroid() {
    return /Android/i.test(navigator.userAgent);
  },

   formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0) + ' F';
  },

  /**
   * Génère le HTML du ticket (méthode originale conservée pour compatibilité)
   */
  generateTicketHTML(saleData, boutiqueInfo, createdBy) {
    const {
      customer,
      items,
      paymentMethod,
      isCredit,
      discount = 0,
      notes,
      saleNumber = this.generateSaleNumber(),
      date = new Date()
    } = saleData;

    const subtotal = this.calculateSubtotal(items);
    const total = subtotal - discount;

    return `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Ticket de Caisse</title>
        <style>
          ${this.getTicketStyles()}
        </style>
      </head>
      <body>
        ${this.getCloseButtonHTML()}
        ${this.getTicketBody(saleData, boutiqueInfo, createdBy)}
      </body>
      </html>
    `;
  },

  /**
   * Génère un numéro de vente unique
   */
  generateSaleNumber() {
    const now = new Date();
    const dateStr = now.getFullYear().toString().slice(-2) + 
                    (now.getMonth() + 1).toString().padStart(2, '0') + 
                    now.getDate().toString().padStart(2, '0');
    const timeStr = now.getHours().toString().padStart(2, '0') + 
                    now.getMinutes().toString().padStart(2, '0');
    const randomStr = Math.floor(Math.random() * 999).toString().padStart(3, '0');
    
    return `V${dateStr}${timeStr}${randomStr}`;
  },

  /**
   * Aperçu du ticket (pour test/prévisualisation)
   */
  /**
 * Aperçu du ticket avec bouton fermeture mobile/tablette
 */
async previewTicket(saleData, boutiqueInfo = {}, createdBy = null) {
  try {
    const ticketHtml = this.generateTicketHTML(saleData, boutiqueInfo, createdBy);
    
    const previewWindow = window.open('', '_blank', 'width=400,height=700,scrollbars=yes');
    
    if (!previewWindow) {
      return { 
        success: false, 
        message: 'Impossible d\'ouvrir l\'aperçu. Vérifiez que les popups ne sont pas bloqués.' 
      };
    }

    previewWindow.document.write(ticketHtml);
    previewWindow.document.close();
    previewWindow.focus();

    return { success: true };
    
  } catch (error) {
    console.error('❌ Erreur aperçu:', error);
    return { 
      success: false, 
      message: 'Erreur lors de l\'aperçu: ' + error.message 
    };
  }
},

/**
 * Détection tablette
 */
isTablet() {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobile))/.test(userAgent);
  return isTablet;
},


/**
 * Header pour aperçu mobile avec bouton fermeture
 */
getMobilePreviewHeader() {
  return `
    <div class="preview-header">
      <div class="preview-title">
        <span class="preview-icon">📋</span>
        <span>Aperçu du ticket</span>
      </div>
      <button class="close-btn" onclick="closePreview()">
        <span class="close-icon">✕</span>
      </button>
    </div>
  `;
},

/**
 * Footer pour aperçu mobile avec boutons d'action
 */
getMobilePreviewFooter() {
  return `
    <div class="preview-footer">
      <button class="action-btn secondary" onclick="closePreview()">
        <span class="btn-icon">✕</span>
        <span>Fermer</span>
      </button>
      <button class="action-btn primary" onclick="printTicket()">
        <span class="btn-icon">🖨️</span>
        <span>Imprimer</span>
      </button>
    </div>
  `;
},

  /**
   * Vérification du support d'impression
   */
  checkPrintSupport() {
    return {
      browserSupport: typeof window !== 'undefined' && 'print' in window,
      popupSupport: typeof window !== 'undefined' && 'open' in window,
      isMobile: this.isMobileDevice(),
      isAndroid: this.isAndroid()
    };
  },

  // ════════════════════════════════════════════════════════════════
  // ✅ RAPPORT DE SESSION DE CAISSE (impression thermique 80mm)
  //    Remplace la capture d'écran (window.print) du CloseSessionModal
  // ════════════════════════════════════════════════════════════════

  /**
   * Imprime le rapport de fermeture de session au format ticket 80mm.
   * @param {Object} sessionData - CashSessionDto (clés snake_case)
   * @param {Object} boutiqueInfo - { name, address, phone, phone1, email, logoUrl }
   */
  async printSessionReport(sessionData, boutiqueInfo = {}) {
    try {
      const reportHtml = this.generateSessionReportHTML(sessionData, boutiqueInfo);
      const support = this.checkPrintSupport();

      const printWindow = window.open('', '_blank', 'width=302,height=600');
      if (!printWindow) {
        throw new Error('Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez que les popups ne sont pas bloqués.');
      }

      printWindow.document.write(reportHtml);
      printWindow.document.close();

      // Android a besoin d'un délai plus long avant window.print()
      const printDelay = support.isAndroid ? 1000 : 500;

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => printWindow.close();
          setTimeout(() => {
            if (!printWindow.closed) printWindow.close();
          }, 1000);
        }, printDelay);
      };

      return { success: true };
    } catch (error) {
      console.error('❌ Erreur impression rapport de session:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'impression du rapport: ' + error.message
      };
    }
  },

  /**
   * Document HTML complet du rapport (réutilise les styles du ticket).
   */
  generateSessionReportHTML(sessionData, boutiqueInfo) {
    return `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Rapport de session</title>
        <style>
          ${this.getTicketStyles()}
        </style>
      </head>
      <body>
        ${this.getCloseButtonHTML()}
        ${this.getSessionReportBody(sessionData, boutiqueInfo)}
      </body>
      </html>
    `;
  },

  /**
   * Formatte une durée en minutes -> "Xh YYmin".
   */
  formatSessionDuration(minutes) {
    const m = parseInt(minutes, 10);
    if (!m || isNaN(m) || m < 0) return '-';
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    if (hours === 0) return `${mins} min`;
    return `${hours}h ${mins.toString().padStart(2, '0')}min`;
  },

  /**
   * Corps du rapport de session (tolérant snake_case / camelCase).
   */
  getSessionReportBody(sessionData, boutiqueInfo = {}) {
    const s = sessionData || {};
    const get = (snake, camel) => (s[snake] !== undefined && s[snake] !== null ? s[snake] : s[camel]);
    const num = (v) => parseFloat(v) || 0;

    const sessionNumber   = get('session_number', 'sessionNumber') || '-';
    const userName        = get('user_name', 'userName') || 'Vendeur';
    const openedAt        = get('opened_at', 'openedAt');
    const closedAt        = get('closed_at', 'closedAt');
    const durationMinutes = get('duration_minutes', 'durationMinutes');
    const forceClosed     = get('force_closed', 'forceClosed') === true;
    const closedByName    = get('closed_by_name', 'closedByName');

    const openingAmount   = num(get('opening_amount', 'openingAmount'));
    const salesCount      = parseInt(get('total_sales_count', 'totalSalesCount'), 10) || 0;
    const cancelledCount  = parseInt(get('total_cancelled_count', 'totalCancelledCount'), 10) || 0;
    const revenue         = num(get('total_revenue', 'totalRevenue'));
    const discount        = num(get('total_discount', 'totalDiscount'));

    const totalCash         = num(get('total_cash', 'totalCash'));
    const totalWave         = num(get('total_wave', 'totalWave'));
    const totalOrangeMoney  = num(get('total_orange_money', 'totalOrangeMoney'));
    const totalCard         = num(get('total_card', 'totalCard'));
    const totalBankTransfer = num(get('total_bank_transfer', 'totalBankTransfer'));
    const totalCheck        = num(get('total_check', 'totalCheck'));
    const totalCredit       = num(get('total_credit', 'totalCredit'));

    const totalExpenses = num(get('total_expenses', 'totalExpenses'));
    const expensesCount = parseInt(get('total_expenses_count', 'totalExpensesCount'), 10) || 0;

    const theoretical = num(get('closing_amount_theoretical', 'closingAmountTheoretical'));
    const realAmount  = num(get('closing_amount_real', 'closingAmountReal'));
    const difference  = num(get('closing_difference', 'closingDifference'));
    const closingNote = get('closing_note', 'closingNote');

    // Afficher uniquement les méthodes avec un montant > 0
    const paymentRows = [
      { label: '💵 Espèces',      value: totalCash },
      { label: '📱 Wave',         value: totalWave },
      { label: '📲 Orange Money', value: totalOrangeMoney },
      { label: '💳 Carte',        value: totalCard },
      { label: '🏦 Virement',     value: totalBankTransfer },
      { label: '🧾 Chèque',       value: totalCheck },
      { label: '⏳ Crédit',       value: totalCredit }
    ].filter(r => r.value > 0);

    const displayDiff = Math.abs(difference) < 1 ? 0 : difference;
    let ecartLabel;
    if (Math.abs(difference) < 1) ecartLabel = '✅ Caisse équilibrée';
    else if (difference < 0)      ecartLabel = '⚠️ Manque en caisse';
    else                          ecartLabel = 'ℹ️ Surplus en caisse';
    const ecartSign = displayDiff > 0 ? '+' : '';

    return `
<div class="ticket">
  <!-- En-tête boutique -->
  <div class="header">
${generateBoutiqueLogoHTML(boutiqueInfo, { width: 140, height: 80 })}
    <div class="shop-name">${boutiqueInfo.name || 'MA BOUTIQUE'}</div>
    <div class="address">${boutiqueInfo.address || 'Dakar, Sénégal'}</div>
    <div class="phone">${
      boutiqueInfo.phone1
        ? `${boutiqueInfo.phone || '+221 XX XXX XX XX'} | ${boutiqueInfo.phone1}`
        : (boutiqueInfo.phone || '+221 XX XXX XX XX')
    }</div>
    ${boutiqueInfo.email ? `<div class="email">${boutiqueInfo.email}</div>` : ''}
  </div>

  <div class="separator">================================</div>

  <div class="items-header">RAPPORT DE SESSION</div>
  ${forceClosed ? `<div class="credit">⚠️ FERMETURE FORCÉE</div>` : ''}

  <!-- Infos session -->
  <div class="sale-info">
    <div class="row"><span>N° Session:</span><span>${sessionNumber}</span></div>
    <div class="row"><span>Vendeur:</span><span>${userName}</span></div>
    ${openedAt ? `<div class="row"><span>Ouverture:</span><span>${this.formatDate(openedAt)} ${this.formatTime(openedAt)}</span></div>` : ''}
    ${closedAt ? `<div class="row"><span>Fermeture:</span><span>${this.formatDate(closedAt)} ${this.formatTime(closedAt)}</span></div>` : ''}
    <div class="row"><span>Durée:</span><span>${this.formatSessionDuration(durationMinutes)}</span></div>
    ${forceClosed && closedByName ? `<div class="row"><span>Fermée par:</span><span>${closedByName}</span></div>` : ''}
  </div>

  <div class="separator">--------------------------------</div>

  <!-- Résumé activité -->
  <div class="items-header">RÉSUMÉ</div>
  <div class="sale-info">
    <div class="row"><span>Nombre de ventes:</span><span>${salesCount}</span></div>
    ${cancelledCount > 0 ? `<div class="row"><span>Ventes annulées:</span><span>${cancelledCount}</span></div>` : ''}
    <div class="row"><span>Chiffre d'affaires:</span><span>${this.formatCurrency(revenue)}</span></div>
    ${discount > 0 ? `<div class="row"><span>Total remises:</span><span>${this.formatCurrency(discount)}</span></div>` : ''}
  </div>

  <div class="separator">--------------------------------</div>

  <!-- Répartition paiements -->
  <div class="items-header">PAR MÉTHODE DE PAIEMENT</div>
  <div class="sale-info">
    ${paymentRows.length > 0
      ? paymentRows.map(r => `<div class="row"><span>${r.label}</span><span>${this.formatCurrency(r.value)}</span></div>`).join('')
      : `<div class="row"><span>Aucun encaissement</span><span></span></div>`}
  </div>

  ${totalExpenses > 0 ? `
  <div class="separator">--------------------------------</div>
  <div class="items-header">SORTIES CAISSE</div>
  <div class="sale-info">
    <div class="row"><span>💸 Dépenses${expensesCount > 0 ? ` (${expensesCount})` : ''}:</span><span>-${this.formatCurrency(totalExpenses)}</span></div>
  </div>` : ''}

  <!-- Calcul théorique -->
  <div class="totals">
    <div class="total-row"><span>Fond d'ouverture:</span><span>${this.formatCurrency(openingAmount)}</span></div>
    <div class="total-row"><span>+ Ventes espèces:</span><span>${this.formatCurrency(totalCash)}</span></div>
    ${totalExpenses > 0 ? `<div class="total-row"><span>- Sorties caisse:</span><span>-${this.formatCurrency(totalExpenses)}</span></div>` : ''}
    <div class="total-row final"><span>= THÉORIQUE:</span><span>${this.formatCurrency(theoretical)}</span></div>
    <div class="total-row"><span>Montant compté:</span><span>${this.formatCurrency(realAmount)}</span></div>
    <div class="total-row final"><span>ÉCART:</span><span>${ecartSign}${this.formatCurrency(displayDiff)}</span></div>
  </div>

  <div class="payment-info">
    <div class="payment-method">${ecartLabel}</div>
  </div>

  ${closingNote ? `
  <div class="separator">--------------------------------</div>
  <div class="notes"><strong>Note:</strong><br>${closingNote}</div>` : ''}

  <div class="separator">================================</div>

  <div class="footer">
    <p class="thank-you">Clôture de caisse</p>
    <p class="return-policy">Document à conserver</p>
  </div>

  <div class="cut-line">- - - - - - - - - - - - - - - - - -</div>
</div>
    `;
  },

// ✅ COPIEZ CE CODE COMPLET dans votre TicketPrintService.js

/**
 * ✅ IMPRESSION 58mm FINALE - TOUT CENTRÉ ET LISIBLE
 */
async printTicket58mm(saleData, boutiqueInfo, createdBy) {
  try {
    
    const ticketHtml = this.generateTicket58mmCentered(saleData, boutiqueInfo, createdBy);
    
    const printWindow = window.open('', '_blank', 'width=200,height=400');
    
    if (!printWindow) {
      throw new Error('Impossible d\'ouvrir la fenêtre d\'impression.');
    }

    printWindow.document.write(ticketHtml);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => printWindow.close();
        setTimeout(() => {
          if (!printWindow.closed) printWindow.close();
        }, 1000);
      }, 500);
    };

    return { success: true, message: 'Ticket 58mm centré imprimé !' };
    
  } catch (error) {
    console.error('❌ Erreur impression 58mm:', error);
    return { success: false, message: 'Erreur : ' + error.message };
  }
},

/**
 * ✅ HTML 58mm CENTRÉ - NOUVEAU NOM DE FONCTION
 */
generateTicket58mmCentered(saleData, boutiqueInfo, createdBy) {
  const {
    customer,
    items = [],
    paymentMethod,
    isCredit,
    notes,
    saleNumber = this.generateSaleNumber(),
    date = new Date(),
    totals // ✅ AJOUT: Support du format avec totals
  } = saleData;

  // ✅ CORRECTION: Récupérer discount avec parseFloat
  const discount = parseFloat(saleData.discount) || parseFloat(totals?.discount) || 0;
  

  const safeItems = Array.isArray(items) ? items : [];
  const subtotal = this.calculateSubtotal(safeItems);
  const total = subtotal - discount;

  return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Ticket 58mm Centré</title>
      <style>
        ${this.getStyles58mmCentered()}
      </style>
    </head>
    <body>
      ${this.getCloseButtonHTML()}
      <div class="ticket">
        <!-- En-tête centré -->
        <div class="header">
          ${this.generateBoutiqueLogoHTML58mm(boutiqueInfo)}
          <div class="shop-name">${boutiqueInfo.name || 'MA BOUTIQUE'}</div>
          <div class="address">${boutiqueInfo.address || 'Dakar, Sénégal'}</div>
          <div class="phone">${
            boutiqueInfo.phone1 
              ? `${boutiqueInfo.phone || '+221 XX XXX XX XX'} | ${boutiqueInfo.phone1}`
              : (boutiqueInfo.phone || '+221 XX XXX XX XX')
          }</div>
          ${boutiqueInfo.email ? `<div class="email">${boutiqueInfo.email}</div>` : ''}
        </div>

        <div class="separator">========================</div>

        <!-- Informations de vente - TOUT CENTRÉ -->
        <div class="sale-info">
          <div class="info-line">
            <strong>N° Ticket:</strong> #${saleNumber}
          </div>
          <div class="info-line">
            <strong>Date:</strong> ${this.formatDate(date)}
          </div>
          <div class="info-line">
            <strong>Heure:</strong> ${this.formatTime(date)}
          </div>
          <div class="info-line">
            <strong>Client:</strong> ${customer?.name || 'Client anonyme'}
          </div>
          ${customer?.phone ? `
          <div class="info-line">
            <strong>Tél:</strong> ${customer.phone}
          </div>` : ''}
          ${customer?.adresse || customer?.address ? `
          <div class="info-line">
            <strong>Adresse:</strong> ${customer.adresse || customer.address}
          </div>` : ''}
          ${createdBy ? `
          <div class="info-line">
            <strong>Vendeur:</strong> ${createdBy.name || createdBy.firstName + ' ' + createdBy.lastName || 'Employé'}
          </div>` : ''}
        </div>

        <div class="separator">------------------</div>

        <!-- Articles - TOUT CENTRÉ -->
        <div class="items-header">ARTICLES</div>
        
        ${safeItems.length > 0 ? safeItems.map(item => `
        <div class="item">
          <div class="item-name">${item.name || item.productName || 'Produit'}</div>
          <div class="item-details">
            ${item.quantity || 0} × ${this.formatCurrency(item.price || item.unitPrice || 0)} = ${this.formatCurrency((item.quantity || 0) * (item.price || item.unitPrice || 0))}
          </div>
        </div>
        `).join('') : '<div class="item"><div class="item-name">Aucun article</div></div>'}

        <!-- Totaux - TOUT CENTRÉ -->
        <div class="totals">
          <div class="total-line">
            <strong>Sous-total:</strong> ${this.formatCurrency(subtotal)}
          </div>
          
          ${discount > 0 ? `
          <div class="total-line">
            <strong>Remise:</strong> -${this.formatCurrency(discount)}
          </div>` : ''}
          
          <div class="total-line final">
            <strong>TOTAL: ${this.formatCurrency(total)}</strong>
          </div>
        </div>

        <!-- Mode de paiement - CENTRÉ -->
        <div class="payment-info">
          <div class="payment-method">
            ${this.getPaymentMethodLabel(paymentMethod)}
          </div>
          ${isCredit ? `
          <div class="credit">
            ⚠️ VENTE À CRÉDIT
          </div>
          ` : ''}
        </div>

        ${notes ? `
          <div class="separator">------------------</div>
          <div class="notes">
            <strong>Notes:</strong><br>
            ${notes}
          </div>
        ` : ''}

        <div class="separator">========================</div>

        <!-- Pied de page - CENTRÉ -->
        <div class="footer">
          <p class="thank-you">Merci de votre visite !</p>
          <p class="return-policy"><strong>${boutiqueInfo.name || 'Notre boutique'}</strong> vous remercie</p>
          <p class="return-policy">pour la confiance renouvelée</p>
        </div>

        <div class="cut-line">- - - - - - - - - - - - - - - - - -</div>
      </div>
    </body>
    </html>
  `;
},

/**
 * ✅ STYLES CSS 58mm CENTRÉ - NOUVEAU NOM DE FONCTION
 */
getStyles58mmCentered() {
  return `
    @page { size: 58mm auto; margin: 0; }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: Arial, sans-serif;
      font-size: 11px;
      line-height: 1.3;
      margin: 0;
      padding: 3px;
      width: 58mm;
      color: black;
      background: white;
      font-weight: 500;
      text-align: center; /* ✅ CENTRAGE GLOBAL */
    }
    
    .ticket {
      width: 100%;
      text-align: center; /* ✅ TOUT CENTRÉ */
    }
    
    /* En-tête - Déjà centré */
    .header {
      text-align: center;
      margin-bottom: 5px;
    }
    
    .shop-name {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 2px;
      letter-spacing: 0.3px;
    }
    
    .address, .phone, .email {
      font-size: 9px;
      margin-bottom: 2px;
      font-weight: 600;
    }
    
    /* Séparateurs */
    .separator {
      text-align: center;
      font-size: 8px;
      margin: 3px 0;
      color: #333;
      font-weight: 600;
    }
    
    /* Informations de vente - CENTRÉ */
    .sale-info {
      margin-bottom: 5px;
      text-align: center;
    }
    
    .info-line {
      font-size: 10px;
      font-weight: 600;
      margin-bottom: 2px;
      text-align: center;
      word-wrap: break-word;
      line-height: 1.4;
    }
    
    .info-line strong {
      font-weight: 800;
      color: #000;
    }
    
    /* En-tête articles */
    .items-header {
      font-weight: bold;
      margin-bottom: 3px;
      font-size: 11px;
      text-align: center;
      background: #f0f0f0;
      padding: 2px;
    }
    
    /* Articles - CENTRÉ */
    .item {
      margin-bottom: 3px;
      border-bottom: 1px dotted #ccc;
      padding-bottom: 2px;
      text-align: center;
    }
    
    .item-name {
      font-weight: bold;
      font-size: 10px;
      color: #000;
      word-break: break-word;
      margin-bottom: 2px;
      text-align: center;
    }
    
    .item-details {
      font-size: 9px;
      font-weight: 600;
      text-align: center;
      display: block;
    }
    
    /* Totaux - CENTRÉ */
    .totals {
      margin-top: 5px;
      border-top: 2px solid #000;
      padding: 3px;
      background: #f8f8f8;
      text-align: center;
    }
    
    .total-line {
      font-size: 10px;
      font-weight: 700;
      margin-bottom: 2px;
      text-align: center;
      line-height: 1.3;
    }
    
    .total-line.final {
      font-weight: 900;
      font-size: 13px;
      border: 2px solid #000;
      padding: 4px;
      margin: 4px 0;
      text-align: center;
      background: #000;
      color: white;
      border-radius: 3px;
    }
    
    /* Paiement */
    .payment-info {
      margin-top: 4px;
      text-align: center;
    }
    
    .payment-method {
      font-weight: bold;
      font-size: 10px;
      margin-bottom: 3px;
      background: #e8f4fd;
      padding: 2px;
      border-radius: 3px;
    }
    
    .credit {
      background: #000;
      color: #fff;
      padding: 3px;
      text-align: center;
      font-weight: bold;
      font-size: 9px;
      margin: 3px 0;
      border-radius: 2px;
    }
    
    /* Notes */
    .notes {
      margin: 3px 0;
      padding: 3px;
      border: 1px solid #ccc;
      font-size: 8px;
      background: #fffef0;
      line-height: 1.4;
      text-align: center;
    }
    
    /* Footer */
    .footer {
      text-align: center;
      margin-top: 5px;
      padding: 3px;
      background: #f5f5f5;
    }
    
    .thank-you {
      font-weight: bold;
      font-size: 11px;
      margin-bottom: 3px;
      color: #2c5530;
    }
    
    .return-policy {
      font-size: 8px;
      margin-bottom: 2px;
      font-weight: 600;
      line-height: 1.3;
    }
    
    /* Ligne de coupe */
    .cut-line {
      text-align: center;
      margin-top: 6px;
      font-size: 8px;
      color: #666;
      letter-spacing: 1px;
      font-weight: 600;
    }
    
    /* Impression */
    @media print {
      body {
        text-align: center !important;
      }
      
      .total-line.final {
        background: #000 !important;
        color: white !important;
        -webkit-print-color-adjust: exact;
      }
    }
  `;
},

/**
 * ✅ Logo pour format 58mm
 */
generateBoutiqueLogoHTML58mm(boutiqueInfo) {
  if (!boutiqueInfo.logo) {
    return '<div class="logo">🏪</div>';
  }
  
  return `
    <div class="logo-container">
      <img src="${boutiqueInfo.logo}" alt="Logo" class="logo-img" style="max-width: 45px; max-height: 25px; object-fit: contain;" />
    </div>
  `;
},

  /**
 * Styles CSS pour l'aperçu avec responsive design
 */
getPreviewStyles(isMobileOrTablet) {
  return `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      background: ${isMobileOrTablet ? '#f5f5f7' : '#ffffff'};
      color: #333;
      line-height: 1.4;
      ${isMobileOrTablet ? `
        padding: 0;
        margin: 0;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      ` : ''}
    }

    /* ===== HEADER MOBILE ===== */
    .preview-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 12px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      position: sticky;
      top: 0;
      z-index: 100;
    }

    .preview-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      font-size: 16px;
    }

    .preview-icon {
      font-size: 18px;
    }

    .close-btn {
      background: rgba(255,255,255,0.2);
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      color: white;
    }

    .close-btn:hover, .close-btn:active {
      background: rgba(255,255,255,0.3);
      transform: scale(1.1);
    }

    .close-icon {
      font-size: 18px;
      font-weight: bold;
    }

    /* ===== CONTENU TICKET ===== */
    .ticket-preview {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: ${isMobileOrTablet ? 'flex-start' : 'center'};
      padding: ${isMobileOrTablet ? '16px' : '20px'};
      ${isMobileOrTablet ? 'overflow-y: auto;' : ''}
    }

    .ticket-container {
      background: white;
      border-radius: ${isMobileOrTablet ? '12px' : '8px'};
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      max-width: ${isMobileOrTablet ? '100%' : '320px'};
      width: 100%;
      overflow: hidden;
      ${isMobileOrTablet ? `
        min-height: auto;
        margin-bottom: 16px;
      ` : ''}
    }

    /* ===== FOOTER MOBILE ===== */
    .preview-footer {
      background: white;
      border-top: 1px solid #e1e5e9;
      padding: 12px 16px;
      display: flex;
      gap: 12px;
      box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
      position: sticky;
      bottom: 0;
    }

    .action-btn {
      flex: 1;
      padding: 12px 16px;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 44px;
    }

    .action-btn.secondary {
      background: #f1f3f4;
      color: #5f6368;
    }

    .action-btn.secondary:hover, .action-btn.secondary:active {
      background: #e8eaed;
      transform: translateY(-1px);
    }

    .action-btn.primary {
      background: linear-gradient(135deg, #4285f4 0%, #1a73e8 100%);
      color: white;
    }

    .action-btn.primary:hover, .action-btn.primary:active {
      background: linear-gradient(135deg, #1a73e8 0%, #1557b0 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(26, 115, 232, 0.4);
    }

    .btn-icon {
      font-size: 16px;
    }

    /* ===== STYLES TICKET (hérités) ===== */
    ${this.getTicketStyles()}

    /* ===== RESPONSIVE ADJUSTMENTS ===== */
    @media (max-width: 480px) {
      .preview-header {
        padding: 10px 12px;
      }
      
      .preview-title {
        font-size: 14px;
      }
      
      .ticket-preview {
        padding: 12px;
      }
      
      .action-btn {
        padding: 10px 12px;
        font-size: 13px;
      }
    }

    /* ===== STYLES POUR DESKTOP ===== */
    ${!isMobileOrTablet ? `
      .ticket-preview {
        min-height: 100vh;
      }
      
      .ticket-container {
        margin: auto;
      }
    ` : ''}
  `;
},

/**
 * Scripts JavaScript pour l'aperçu
 */
getPreviewScripts(isMobileOrTablet) {
  return `
    // Fonction pour fermer l'aperçu
    function closePreview() {
      window.close();
    }
    
    // Fonction pour imprimer le ticket
    function printTicket() {
      window.print();
    }
    
    ${isMobileOrTablet ? `
      // Gestion spéciale mobile/tablette
      
      // Empêcher le zoom sur double-tap pour les boutons
      let lastTouchEnd = 0;
      document.addEventListener('touchend', function (event) {
        var now = (new Date()).getTime();
        if (now - lastTouchEnd <= 300) {
          event.preventDefault();
        }
        lastTouchEnd = now;
      }, false);
      
      // Support des gestes de swipe pour fermer
      let startY = 0;
      let startX = 0;
      
      document.addEventListener('touchstart', function(e) {
        startY = e.touches[0].clientY;
        startX = e.touches[0].clientX;
      }, false);
      
      document.addEventListener('touchmove', function(e) {
        if (!startY || !startX) return;
        
        let diffY = startY - e.touches[0].clientY;
        let diffX = startX - e.touches[0].clientX;
        
        // Swipe vers le bas pour fermer
        if (diffY < -100 && Math.abs(diffX) < 50) {
          closePreview();
        }
      }, false);
      
      // Feedback haptique sur les boutons (si supporté)
      document.querySelectorAll('.action-btn, .close-btn').forEach(btn => {
        btn.addEventListener('click', function() {
          if (navigator.vibrate) {
            navigator.vibrate(10);
          }
        });
      });
      
    ` : `
      // Gestion desktop
      
      // Raccourcis clavier
      document.addEventListener('keydown', function(e) {
        // Echap pour fermer
        if (e.key === 'Escape') {
          closePreview();
        }
        // Ctrl+P pour imprimer
        else if (e.ctrlKey && e.key === 'p') {
          e.preventDefault();
          printTicket();
        }
      });
    `}
    
    // Log pour debug
  `;
},

  /**
   * Utilitaires de formatage
   */
  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0
    }).format(price || 0) + ' XOF';
  },

  formatDate(date) {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  },

  formatTime(date) {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date));
  },

  calculateSubtotal(items) {
  // ✅ CORRECTION: Protection contre items undefined ou null
  if (!items || !Array.isArray(items)) {
    console.warn('⚠️ Items invalid pour calculateSubtotal:', items);
    return 0;
  }

  return items.reduce((total, item) => {
    const price = item.price || item.unitPrice || 0;
    const quantity = item.quantity || 0;
    return total + (price * quantity);
  }, 0);
},

  getPaymentMethodLabel(method) {
    const methods = {
      'CASH': '💵 Espèces',
      'WAVE': '📱 Wave',
      'ORANGE_MONEY': '📲 Orange Money',
      'CREDIT': '💳 Crédit',
      'CHECK': '🧾 Chèque',
      'BANK_TRANSFER': '🏦 Virement Bancaire',
      'TRANSFER': '🏦 Virement Bancaire',
      'CARD': '🏦 Virement Bancaire',
      'MIXED': '🔄 Paiement mixte',

    };
    return methods[method] || method;
  }
};



export default TicketPrintService;